if (!customElements.get('byte-rich-text-expand')) {
  const HEIGHT_TRANSITION_MS = 450;

  class ByteRichTextExpand extends HTMLElement {
    constructor() {
      super();

      this.isExpanded = false;
      this.isAnimating = false;
      this.hasOverflow = false;
      this.collapsedHeight = 0;
      this.expandedHeight = 0;
      this.transitionTimeout = 0;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.resizeObserver = null;
      this.pendingTransitionCallback = null;
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#updateOverflowState();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-rich-text-action]');
      if (!actionTrigger) return;

      if (actionTrigger.dataset.richTextAction === 'toggle') {
        this.toggle();
      }
    }

    handleResize() {
      if (this.isAnimating) return;
      this.#updateOverflowState();
    }

    handleTransitionEnd(event) {
      if (event.propertyName !== 'max-height') return;
      this.#finishHeightTransition();
    }

    toggle() {
      if (this.isAnimating) return;

      if (this.isExpanded) {
        this.collapse();
        return;
      }

      this.expand();
    }

    expand() {
      if (!this.content) return;

      this.isExpanded = true;
      this.classList.add('is-expanded');
      this.#syncToggleState();
      this.#animateHeight(this.content.scrollHeight, () => {
        this.#setMaxHeight('none', false);
      });
    }

    collapse() {
      if (!this.content) return;

      this.#measureHeights();
      this.isExpanded = false;
      this.classList.remove('is-expanded');
      this.#syncToggleState();
      this.#animateHeight(this.collapsedHeight);
    }

    #cacheElements() {
      this.content = this.querySelector('[data-rich-text-content]');
      this.toggleButton = this.querySelector('[data-rich-text-action="toggle"]');
      this.moreLabel = this.querySelector('[data-rich-text-more]');
      this.lessLabel = this.querySelector('[data-rich-text-less]');
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      this.resizeObserver = new ResizeObserver(this.boundHandleResize);
      this.resizeObserver.observe(this);

      if (this.content) {
        this.content.addEventListener('transitionend', this.boundHandleTransitionEnd);
      }

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (!this.isConnected) return;
          this.#updateOverflowState();
        });
      }
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      this.#clearHeightTransition();

      if (this.content) {
        this.content.removeEventListener('transitionend', this.boundHandleTransitionEnd);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }

    #updateOverflowState() {
      if (!this.content) return;
      if (this.isExpanded) return;

      this.#measureHeights();
      this.#setMaxHeight(this.hasOverflow ? `${this.collapsedHeight}px` : 'none', false);
    }

    #measureHeights() {
      const content = this.content;
      const clone = content.cloneNode(true);
      const visibleLines = this.#getVisibleLines();

      clone.setAttribute('aria-hidden', 'true');
      clone.style.cssText = [
        'position: absolute',
        'left: 0',
        'right: 0',
        'top: 0',
        'visibility: hidden',
        'pointer-events: none',
        'height: auto',
        'max-height: none',
        'overflow: visible',
        'display: block',
        '-webkit-line-clamp: unset',
        'line-clamp: unset'
      ].join(';');

      content.parentNode.appendChild(clone);
      const expandedHeight = clone.scrollHeight;

      clone.style.display = '-webkit-box';
      clone.style.webkitBoxOrient = 'vertical';
      clone.style.webkitLineClamp = String(visibleLines);
      clone.style.overflow = 'hidden';
      const collapsedHeight = clone.clientHeight;

      clone.remove();

      this.expandedHeight = expandedHeight;
      this.collapsedHeight = collapsedHeight;
      this.hasOverflow = expandedHeight > collapsedHeight + 1;
      this.classList.toggle('has-overflow', this.hasOverflow);
    }

    #getVisibleLines() {
      const visibleLines = Number.parseInt(this.style.getPropertyValue('--rich-text-expand-lines'), 10);

      if (Number.isFinite(visibleLines) && visibleLines > 0) {
        return visibleLines;
      }

      return 6;
    }

    #animateHeight(endHeight, onComplete) {
      const startHeight = this.content.clientHeight;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#clearHeightTransition();
      this.pendingTransitionCallback = onComplete || null;

      if (prefersReducedMotion || startHeight === endHeight) {
        this.#setMaxHeight(`${endHeight}px`, false);
        this.#finishHeightTransition();
        return;
      }

      this.isAnimating = true;
      this.#setMaxHeight(`${startHeight}px`, false);
      this.content.offsetHeight;
      this.#setMaxHeight(`${endHeight}px`, true);

      this.transitionTimeout = window.setTimeout(() => {
        this.#finishHeightTransition();
      }, HEIGHT_TRANSITION_MS + 50);
    }

    #finishHeightTransition() {
      if (!this.isAnimating && !this.pendingTransitionCallback) return;

      this.#clearHeightTransition();
      this.isAnimating = false;

      const pendingCallback = this.pendingTransitionCallback;
      this.pendingTransitionCallback = null;

      if (pendingCallback) {
        pendingCallback();
      }
    }

    #setMaxHeight(maxHeight, shouldAnimate) {
      if (!shouldAnimate) {
        this.content.style.transition = 'none';
      } else {
        this.content.style.transition = '';
      }

      this.content.style.maxHeight = maxHeight;

      if (!shouldAnimate) {
        this.content.offsetHeight;
        this.content.style.transition = '';
      }
    }

    #clearHeightTransition() {
      if (this.transitionTimeout) {
        window.clearTimeout(this.transitionTimeout);
        this.transitionTimeout = 0;
      }
    }

    #syncToggleState() {
      if (!this.toggleButton) return;

      this.toggleButton.setAttribute('aria-expanded', String(this.isExpanded));

      if (this.moreLabel) {
        this.moreLabel.hidden = this.isExpanded;
      }

      if (this.lessLabel) {
        this.lessLabel.hidden = !this.isExpanded;
      }
    }
  }

  customElements.define('byte-rich-text-expand', ByteRichTextExpand);
}
